import {AuthModel} from './_models'
import {isOnPremisesInstallation} from './onPremisesAccess'
import {isStaff, staffCan, StaffSection} from './staffAccess'

type PostLoginUser = Partial<
  Pick<AuthModel, 'rl' | 'cpi' | 'isOnPremises' | 'cpl' | 'support' | 'staff'>
>

export const DEFAULT_POST_LOGIN_PATH = '/dashboard'
export const STAFF_BUSINESSES_PATH = '/apps/taxpayer-management/companies'

/**
 * Where a staff session lands (docs/staff-access.md): «کسب و کارها» when the CRM role grants it, otherwise the first
 * granted section's page, in this order; a session with none of them (only «ابزارهای نگهداری») opens the dashboard.
 */
export const STAFF_LANDING_PATHS: ReadonlyArray<[StaffSection, string]> = [
  ['businesses', STAFF_BUSINESSES_PATH],
  ['invoices', '/apps/invoice-management/invoices'],
  ['reports', '/reports'],
  ['logs', '/apps/log-management/logs'],
  ['users', '/apps/users'],
  ['partners', '/apps/partners'],
  ['monitoring', '/apps/monitoring'],
  ['sms', '/apps/sms-log-management'],
  ['callCenter', '/callcenter/live'],
  ['services', '/apps/service-pricing'],
  ['payments', '/apps/wallet-settings'],
  ['accountingSync', '/apps/accounting-sync/monitor'],
  ['catalog', '/apps/stuffid-catalog-import'],
  ['automaticInvoice', '/apps/product-management/products'],
  ['taxWorkspace', '/tax-workspace/sales-invoices'],
  ['supplyChain', '/supply-chain/admin/cases'],
]

export const getStaffLandingPath = (user?: PostLoginUser | null): string => {
  const granted = STAFF_LANDING_PATHS.find(([section]) => staffCan(user, section))
  return granted ? granted[1] : DEFAULT_POST_LOGIN_PATH
}
/** «کسب‌وکارهای من»: where a user of more than one business lands (the user, 2026-10-26). */
export const MY_BUSINESSES_PATH = '/my-businesses'
/** Isun's business (خدمات همراه آیسان لوتوس), whose users work from its own report. */
export const ISUN_COMPANY_ID = 1021
export const ISUN_POST_LOGIN_PATH = '/reports/isun-daily'

/** A user of more than one business, who picks one on «کسب‌وکارهای من» (not staff, nor a support session). */
export const hasManyBusinesses = (user?: PostLoginUser | null) =>
  Boolean(user) && !isStaff(user) && !user?.support && (user?.cpl?.length || 0) > 1

// Staff (opened from the CRM) work across businesses, so they land on their first granted section
// (getStaffLandingPath). Isun's users land on their report, which opens on the current season (the user,
// 2026-10-08). A user of more than one business lands on «کسب‌وکارهای من» and goes in from there, and
// a user of one business goes straight in (the user, 2026-10-26). Every other role keeps the dashboard
// of the selected business, including support sessions (the backend issues them as role 0) and role -1,
// which BusinessRequired sends on to registration. An on-premises install has no Isun report, whatever
// business carries Isun's id there. This only chooses where a login lands; the dashboard stays reachable
// for everyone.
export const getPostLoginPath = (user?: PostLoginUser | null): string => {
  if (isStaff(user)) return getStaffLandingPath(user)
  if (user?.cpi === ISUN_COMPANY_ID && user.rl !== -1 && !isOnPremisesInstallation(user))
    return ISUN_POST_LOGIN_PATH
  if (hasManyBusinesses(user)) return MY_BUSINESSES_PATH
  return DEFAULT_POST_LOGIN_PATH
}

/** «پیشخوان همکاری», where a partner's business opens (the user, 2026-10-28). */
export const PARTNER_WORKSPACE_PATH = '/apps/partner-workspace'
/**
 * Where a business is entered: BusinessLanding asks whether the selected business is a partner and opens
 * «پیشخوان همکاری» for one, the dashboard for every other.
 */
export const BUSINESS_LANDING_PATH = '/start'

/** The post-login path, with the dashboard replaced by the business landing so a partner opens on its workspace. */
export const getEntryPath = (user?: PostLoginUser | null): string => {
  const path = getPostLoginPath(user)
  return path === DEFAULT_POST_LOGIN_PATH ? BUSINESS_LANDING_PATH : path
}

/**
 * Whether the business landing may ask about a partner at all: not without a selected business, in a support
 * session (the workspace is not offered there), on an on-premises install (no partners), for role -1
 * (BusinessRequired sends it on to registration) or for staff.
 */
export const mayOpenPartnerWorkspace = (user?: PostLoginUser | null) =>
  Boolean(user?.cpi) &&
  !user?.support &&
  !isOnPremisesInstallation(user) &&
  user?.rl !== -1 &&
  !isStaff(user)
